import React, {useState} from 'react';
import {compose, graphql} from 'react-apollo';
import posed, {PoseGroup} from 'react-pose';
import gql from 'graphql-tag';
import {useFormState} from 'react-use-form-state';
import {Button, Input, EqualColumns, Loading, WaveText} from 'ui-kit';

import * as styles from './UpdatePasswordForm.module.css';

const StatusAppear = posed.div({
  enter: {
    x: 0,
    opacity: 1,
    transition: {
      x: {ease: 'easeOut'},
    },
  },
  beforeEnter: {
    x: -10,
    opacity: 0,
    transition: {
      x: {ease: 'easeIn'},
    },
  },
  exit: {
    x: 10,
    opacity: 0,
    transition: {
      x: {ease: 'easeIn'},
    },
  },
});

function UpdatePasswordForm({mutate}) {
  const [loading, setLoading] = useState(false);
  const [showSuccessText, setShowSuccessText] = useState(false);
  const [errors, setErrors] = useState({});
  const [formState, {password}] = useFormState({
    oldPassword: '',
    password: '',
    passwordConfirmation: '',
  });

  async function handleSubmit(event) {
    event.preventDefault();

    setLoading(true);
    setErrors({});

    try {
      const {
        data: {updateStaffMemberPassword},
      } = await mutate({
        variables: {
          input: {
            oldPassword: formState.values.oldPassword,
            password: formState.values.password,
            passwordConfirmation: formState.values.passwordConfirmation,
          },
        },
      });

      const {errors} = updateStaffMemberPassword;

      if (errors.length > 0) {
        const clientErrors = errors.reduce((allErrors, {path, message}) => {
          if (allErrors[path] == null) {
            return {
              ...allErrors,
              [path]: message,
            };
          }

          return {
            ...allErrors,
            [path]: `${allErrors[path]}; ${message}`,
          };
        }, {});

        setErrors(clientErrors);
      } else {
        formState.clear();
        setShowSuccessText(true);

        setTimeout(() => setShowSuccessText(false), 2000);
      }

      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }

  return (
    <form method="post" onSubmit={handleSubmit}>
      <EqualColumns columns={1}>
        <Input
          label="Old password"
          errorMessage={errors.oldPassword}
          noPadding
          {...password('oldPassword')}
        />
      </EqualColumns>
      <EqualColumns>
        <Input
          label="New password"
          errorMessage={errors.password}
          noPadding
          {...password('password')}
        />
        <Input
          label="Confirm new password"
          errorMessage={errors.passwordConfirmation}
          noPadding
          {...password('passwordConfirmation')}
        />
      </EqualColumns>

      <div className={styles.Footer}>
        <Button type="submit" disabled={loading}>
          {loading ? <Loading immediate /> : 'Update password'}
        </Button>
        <div className={styles.Status}>
          <PoseGroup preEnterPose="beforeEnter">
            {showSuccessText ? (
              <StatusAppear key="success">
                <WaveText text="Success!" color="green" />
              </StatusAppear>
            ) : null}
          </PoseGroup>
        </div>
      </div>
    </form>
  );
}

export default compose(
  graphql(
    gql`
      mutation UpdatePassword($input: UpdateStaffMemberPasswordInput!) {
        updateStaffMemberPassword(input: $input) {
          errors {
            path
            message
          }
          staffMember {
            id
          }
        }
      }
    `,
  ),
)(UpdatePasswordForm);
